/**
 * Director — the finite state machine that walks the surface graph.
 *
 * Implements the six-gesture transition grammar:
 *
 *   - push      : add surface to stack (deepen into a flow)
 *   - pop       : remove top surface (back out of a flow)
 *   - lift      : raise a diminished prior surface back to active
 *   - spawn     : place a transient surface over a persistent environment
 *   - bridge    : cross-environment transition (pop stack, push new env)
 *   - interrupt : priority preemption (incoming call, alert)
 *
 * Every gesture emits a telemetry event with the surface ids, timing, and
 * the answer/state payload at transition. The full gesture log is itself
 * eventually persisted as a stream of signed envelopes (audit trail of
 * user navigation).
 */

import type { SurfaceRegistry } from '../surface/Surface';

export type Gesture = 'push' | 'pop' | 'lift' | 'spawn' | 'bridge' | 'interrupt';

export interface GestureEvent {
  gesture: Gesture;
  from: string | null;
  to: string;
  at: number;
  detail?: string;
}

export interface DirectorState {
  /** Currently active surface id. */
  active: string | null;
  /** Stack of surfaces (deepest first, active last). */
  stack: string[];
  /** Ambient surfaces always shown. */
  ambient: string[];
}

export class Director {
  #registry: SurfaceRegistry;
  #state: DirectorState = { active: null, stack: [], ambient: [] };
  #log: GestureEvent[] = [];
  #listeners = new Set<(s: DirectorState) => void>();

  constructor(registry: SurfaceRegistry) {
    this.#registry = registry;
  }

  state(): DirectorState { return { ...this.#state, stack: [...this.#state.stack], ambient: [...this.#state.ambient] }; }

  log(): readonly GestureEvent[] { return this.#log; }

  subscribe(fn: (s: DirectorState) => void): () => void {
    this.#listeners.add(fn);
    return () => this.#listeners.delete(fn);
  }

  // ─── gestures ─────────────────────────────────────────

  push(surfaceId: string, detail?: string): void {
    this.#assertExists(surfaceId);
    const from = this.#state.active;
    this.#state.stack.push(surfaceId);
    this.#state.active = surfaceId;
    this.#emit('push', from, surfaceId, detail);
  }

  pop(detail?: string): void {
    if (this.#state.stack.length === 0) return;
    const from = this.#state.active;
    this.#state.stack.pop();
    this.#state.active = this.#state.stack[this.#state.stack.length - 1] ?? null;
    this.#emit('pop', from, this.#state.active ?? '∅', detail);
  }

  lift(surfaceId: string, detail?: string): void {
    const idx = this.#state.stack.indexOf(surfaceId);
    if (idx === -1) throw new Error(`Cannot lift ${surfaceId}: not in stack`);
    const from = this.#state.active;
    // Move surface to top of stack
    this.#state.stack.splice(idx, 1);
    this.#state.stack.push(surfaceId);
    this.#state.active = surfaceId;
    this.#emit('lift', from, surfaceId, detail);
  }

  spawn(surfaceId: string, detail?: string): void {
    this.#assertExists(surfaceId);
    const def = this.#registry.get(surfaceId);
    if (def?.surfaceClass !== 'transient') {
      throw new Error(`Cannot spawn ${surfaceId}: must be transient class`);
    }
    const from = this.#state.active;
    this.#state.stack.push(surfaceId);
    this.#state.active = surfaceId;
    this.#emit('spawn', from, surfaceId, detail);
  }

  bridge(surfaceId: string, detail?: string): void {
    this.#assertExists(surfaceId);
    const from = this.#state.active;
    // Bridge clears the stack and pushes the new environment
    this.#state.stack = [surfaceId];
    this.#state.active = surfaceId;
    this.#emit('bridge', from, surfaceId, detail);
  }

  /**
   * Replace the entire surface stack atomically. Used by SASS per-tab
   * history restoration — when the user switches tabs, the outgoing
   * tab's stack is saved and the incoming tab's stored stack is
   * restored in one shot rather than via repeated bridge+push calls.
   *
   * The active surface becomes the top of the new stack. All entries
   * must exist in the surface registry (throws otherwise). Empty stack
   * is rejected — use `bridge` to land somewhere if you don't have a
   * stack history.
   */
  setStack(newStack: string[], detail?: string): void {
    if (newStack.length === 0) throw new Error('setStack: cannot set an empty stack — use bridge() to land on a single surface');
    for (const id of newStack) this.#assertExists(id);
    const from = this.#state.active;
    this.#state.stack = [...newStack];
    this.#state.active = newStack[newStack.length - 1]!;
    this.#emit('bridge', from, this.#state.active, detail);
  }

  interrupt(surfaceId: string, detail?: string): void {
    this.#assertExists(surfaceId);
    const from = this.#state.active;
    this.#state.stack.push(surfaceId);
    this.#state.active = surfaceId;
    this.#emit('interrupt', from, surfaceId, detail);
  }

  setAmbient(ids: string[]): void {
    ids.forEach((id) => this.#assertExists(id));
    this.#state.ambient = ids;
    this.#listeners.forEach((l) => l(this.state()));
  }

  // ─── internals ────────────────────────────────────────

  #emit(gesture: Gesture, from: string | null, to: string, detail?: string): void {
    const ev: GestureEvent = { gesture, from, to, at: Date.now() };
    if (detail !== undefined) ev.detail = detail;
    this.#log.push(ev);
    this.#listeners.forEach((l) => l(this.state()));
  }

  #assertExists(surfaceId: string): void {
    if (!this.#registry.get(surfaceId)) {
      throw new Error(`Unknown surface: ${surfaceId}`);
    }
  }
}
